// RDP simplification + Catmull-Rom spline utilities for smooth track rendering.

/**
 * Ramer-Douglas-Peucker simplification.
 *
 * @param {Array} points – objects with `pos_x` and `pos_z` properties
 * @param {number} epsilon – max perpendicular distance tolerance (world units)
 * @returns {number[]} indices of kept points
 */
export function rdpSimplify(points, epsilon = 0.8) {
  if (points.length <= 2) return points.map((_, i) => i)

  const keep = new Uint8Array(points.length)
  keep[0] = 1
  keep[points.length - 1] = 1

  _rdpRange(points, 0, points.length - 1, epsilon, keep)

  const indices = []
  for (let i = 0; i < keep.length; i++) {
    if (keep[i]) indices.push(i)
  }
  return indices
}

function _rdpRange(points, lo, hi, epsilon, keep) {
  if (hi - lo < 2) return

  let maxDist = 0
  let maxIdx = lo

  const ax = points[lo].pos_x,
    az = points[lo].pos_z
  const bx = points[hi].pos_x,
    bz = points[hi].pos_z
  const dx = bx - ax,
    dz = bz - az
  const lenSq = dx * dx + dz * dz

  for (let i = lo + 1; i < hi; i++) {
    const px = points[i].pos_x - ax
    const pz = points[i].pos_z - az
    let dist
    if (lenSq < 1e-12) {
      dist = Math.sqrt(px * px + pz * pz)
    } else {
      const cross = Math.abs(px * dz - pz * dx)
      dist = cross / Math.sqrt(lenSq)
    }
    if (dist > maxDist) {
      maxDist = dist
      maxIdx = i
    }
  }

  if (maxDist > epsilon) {
    keep[maxIdx] = 1
    _rdpRange(points, lo, maxIdx, epsilon, keep)
    _rdpRange(points, maxIdx, hi, epsilon, keep)
  }
}

/**
 * Compute cubic Bezier control points for one Catmull-Rom segment (p1→p2),
 * using centripetal parameterization (alpha=0.5).
 *
 * @param {number} p0x, p0y – preceding point
 * @param {number} p1x, p1y – start of segment
 * @param {number} p2x, p2y – end of segment
 * @param {number} p3x, p3y – following point
 * @returns {{ cp1x, cp1y, cp2x, cp2y }} control points for ctx.bezierCurveTo
 */
export function catmullRomToBezier(p0x, p0y, p1x, p1y, p2x, p2y, p3x, p3y) {
  // Centripetal knot intervals
  const dt0 = Math.pow(Math.hypot(p1x - p0x, p1y - p0y), 0.5) || 1e-6
  const dt1 = Math.pow(Math.hypot(p2x - p1x, p2y - p1y), 0.5) || 1e-6
  const dt2 = Math.pow(Math.hypot(p3x - p2x, p3y - p2y), 0.5) || 1e-6

  // Tangent at p1
  const t1x = (p1x - p0x) / dt0 - (p2x - p0x) / (dt0 + dt1) + (p2x - p1x) / dt1
  const t1y = (p1y - p0y) / dt0 - (p2y - p0y) / (dt0 + dt1) + (p2y - p1y) / dt1

  // Tangent at p2
  const t2x = (p2x - p1x) / dt1 - (p3x - p1x) / (dt1 + dt2) + (p3x - p2x) / dt2
  const t2y = (p2y - p1y) / dt1 - (p3y - p1y) / (dt1 + dt2) + (p3y - p2y) / dt2

  // Convert to cubic Bezier control points
  return {
    cp1x: p1x + (t1x * dt1) / 3,
    cp1y: p1y + (t1y * dt1) / 3,
    cp2x: p2x - (t2x * dt1) / 3,
    cp2y: p2y - (t2y * dt1) / 3,
  }
}

/**
 * Draw a spline path through simplified points using Catmull-Rom curves.
 * Calls ctx.moveTo/bezierCurveTo/lineTo — does NOT call beginPath/stroke.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {number[]} indices – simplified point indices
 * @param {Function} pointProject – index → [screenX, screenY] projector
 */
export function traceSplinePath(ctx, indices, pointProject) {
  if (indices.length < 2) return

  const p0 = pointProject(indices[0])
  ctx.moveTo(p0[0], p0[1])

  for (let k = 0; k < indices.length - 1; k++) {
    const i1 = indices[k]
    const i2 = indices[k + 1]
    const i0 = k > 0 ? indices[k - 1] : i1
    const i3 = k < indices.length - 2 ? indices[k + 2] : i2

    const pi2 = pointProject(i2)
    if (i0 === i1 || i2 === i3) {
      ctx.lineTo(pi2[0], pi2[1])
    } else {
      const pi0 = pointProject(i0)
      const pi1 = pointProject(i1)
      const pi3 = pointProject(i3)
      const { cp1x, cp1y, cp2x, cp2y } = catmullRomToBezier(
        pi0[0],
        pi0[1],
        pi1[0],
        pi1[1],
        pi2[0],
        pi2[1],
        pi3[0],
        pi3[1],
      )
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, pi2[0], pi2[1])
    }
  }
}
